<template>
  <VPLabel label="Image" />
  <div class="announce-form-image-section">
    <input
      ref="imgInput"
      type="file"
      class="hidden"
      multiple
      accept="image/jpeg, image/png, image/webp"
      @change="onImageSelected"
    >

    <div
      class="vp-file-upload"
      :class="[allAttachmentsReady ? 'cursor-pointer' : 'cursor-wait hover:bg-vp-gray-light-2']"
      @click="addImage"
    >
      <div
        type="button"
        class="flex flex-col items-center"
      >
        <Image
          class="text-4xl"
          style
        />
        <span class="uppercase font-semibold text-xs leading-4 mt-2">{{
          allAttachmentsReady ? 'Add image' : 'Processing'
        }}</span>
      </div>
    </div>
    <ImageSectionSkeleton
      v-if="!allAttachmentsReady"
      :attachment-length="attachmentIds.length"
    />
    <div
      v-for="(attachment, $index) in attachmentKeys"
      v-else
      :key="$index"
      class="vp-file-preview group"
      @click="onFilePreviewClick(attachment, $index)"
    >
      <p
        v-if="attachment.pending && pendingAttachments[attachment.key].uploadProgress < 100.0"
        class="rounded bg-gray-200 p-2"
      >
        Status: {{ pendingAttachments[attachment.key].uploadProgress.toFixed(2) }}%
      </p>
      <p
        v-if="hasAttachmentFailed(attachment)"
        class="rounded text-red-600 bg-red-200 w-full text-center"
      >
        Upload failed
      </p>
      <img
        class="w-100 h-100 object-cover"
        :src="attachmentUrl(attachment)"
        alt=""
      >
      {{ attachment }}
      <Star
        v-if="isAttachmentHighlighted(attachment)"
        class="preview-highlighted"
      />
      <div class="preview-group hidden group-hover:flex flex-row">
        <Pencil
          v-if="isAttachmentReady(attachment)"
          class="preview-edit"
          @click.prevent.stop="editImg(attachment)"
        />
        <TrashCanOutline
          class="preview-delete"
          @click.prevent.stop="deleteImg($index, attachment)"
        />
      </div>
      <VPButton
        v-if="!isAttachmentHighlighted(attachment) && !hasAttachmentFailed(attachment)"
        theme="primary"
        variant="outline"
        size="md"
        class="preview-set-cover hidden group-hover:block h-7 w-32"
        @click.prevent.stop="setCover(attachment)"
      >
        Set as cover
      </VPButton>
    </div>

    <VPModal
      v-model:show="toggleImagePreview"
      title="Preview full size"
      size="lg"
    >
      <div class="m-5">
        <p
          v-if="
            activeAttachmentPreview?.pending && pendingAttachments[activeAttachmentPreview?.key].uploadProgress < 100.0
          "
          class="rounded bg-gray-200 p-2"
        >
          Status: {{ pendingAttachments[activeAttachmentPreview.key].uploadProgress.toFixed(2) }}%
        </p>
        <img
          v-if="activeAttachmentPreview"
          class="image-preview-full"
          :src="attachmentUrl(activeAttachmentPreview)"
          alt=""
        >
      </div>

      <template #commands="slotProps">
        <div class="flex justify-end space-x-2 mt-auto">
          <VPButton
            class="pl-4 pr-4"
            size="lg"
            variant="solid"
            max-width="8.75rem"
            @click.prevent="editImg(activeAttachmentPreview)"
          >
            Edit
          </VPButton>
          <VPButton
            class="pl-4 pr-4"
            size="lg"
            variant="solid"
            max-width="8.75rem"
            @click.prevent="deleteImg(activeAttachmentIndexPreview, activeAttachmentPreview)"
          >
            Delete
          </VPButton>
          <VPButton
            class="pl-4 pr-4"
            size="lg"
            variant="solid"
            max-width="8.75rem"
            @click.prevent="setCover(activeAttachmentPreview)"
          >
            Set as cover
          </VPButton>
          <VPButton
            class="ml-5 pl-4 pr-4"
            size="lg"
            variant="outline"
            max-width="8.75rem"
            @click.prevent="slotProps.close"
          >
            Close
          </VPButton>
        </div>
      </template>
    </VPModal>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Image, Pencil, TrashCanOutline, Star } from 'mdue';
import { useAttachmentStore } from '@/stores/attachment';
import { useEventStore } from '@/stores/event';
import VPLabel from '@/components/form/VPLabel.vue';
import VPButton from '@/components/form/VPButton.vue';
import VPModal from '@/components/form/VPModal.vue';
import ImageSectionSkeleton from '@/components/announce-modal/ImageSectionSkeleton.vue';
import type { AnnounceAttachment, AnnounceAttachmentInput, Maybe } from '@/generated/graphql';
import type { ImageSource } from '@pqina/pintura';
import { openDefaultEditor } from '@pqina/pintura';
import '@pqina/pintura/pintura.css';
import { AttachmentStatus } from '@/types/Attachment';

interface AttachmentKey {
  pending: boolean;
  key: string | number;
  id: Maybe<number>;
}

interface Props {
  attachments: AnnounceAttachment[];
}
const props = withDefaults(defineProps<Props>(), {
  attachments: () => [],
  readyAttachments: () => [],
});
const emit = defineEmits<{
  (e: 'update:ready-attachments', att: AnnounceAttachmentInput[], formDirty: boolean): void;
}>();

const imgInput = ref<HTMLInputElement | undefined>();
const toggleImagePreview = ref(false);
const activeAttachmentPreview = ref<AttachmentKey | null>(null);
const activeAttachmentIndexPreview = ref<number | null>(null);
const deleteAttachmentIds = ref<number[]>([]);
const highlightedImageId = ref<number | null>(null);
const attachmentIds = ref<number[]>([]);

const eventStore = useEventStore();
const attachmentStore = useAttachmentStore();
const { allAttachmentsReady } = storeToRefs(attachmentStore);
const attachmentKeys = ref<AttachmentKey[]>([]);
const attachmentPreviews = ref<{ [key: string]: { loaded: boolean; data?: string } }>({});

const pendingAttachments = computed(() =>
  Object.fromEntries(pendingAttachmentKeys.value.map(k => [k, attachmentStore.pendingAttachments[k]])),
);
const pendingAttachmentKeys = computed(() => attachmentKeys.value.filter(ak => ak.pending).map(ak => ak.key));
const selectedEvent = computed(() => eventStore.selectedEvent);

const prepareReadyAttachments = (ids: number[], formDirty: boolean = true) => {
  const readyAttachments: AnnounceAttachmentInput[] = [];
  for (const id of ids) {
    const originalAtt = props.attachments.find(a => Number(a.attachment.id) === id);
    if (!originalAtt) {
      const pendingAttachmentsReference = attachmentStore.pendingAttachments;
      for (const key in pendingAttachmentsReference) {
        if (pendingAttachmentsReference[key].attachment.id === id) {
          const pendingAtt = {
            attachmentId: pendingAttachmentsReference[key].attachment.id,
            highlighted: highlightedImageId.value === id,
            _destroy: deleteAttachmentIds.value.includes(id),
          };
          readyAttachments.push(pendingAtt);
        }
      }
      continue;
    } else {
      readyAttachments.push({
        attachmentId: originalAtt.attachment.id,
        highlighted: highlightedImageId.value === id,
        _destroy: deleteAttachmentIds.value.includes(id),
      });
    }
  }
  attachmentIds.value = ids;
  if (readyAttachments.length === 1) {
    readyAttachments[0].highlighted = true;
  }
  emit('update:ready-attachments', readyAttachments, formDirty);
};

const setAttachmentProperties = () => {
  const ids: number[] = [];
  attachmentKeys.value = props.attachments.map((att, idx) => {
    ids.push(Number(att.attachment.id));
    return {
      pending: false,
      key: idx,
      id: Number.parseInt(att?.attachment.id) || null,
    };
  });
  initHighlightedImageId();
  prepareReadyAttachments(ids, false);
};

const isAttachmentHighlighted = (ak: AttachmentKey) => {
  if (ak.pending) {
    const att = pendingAttachments.value[ak.key].attachment;
    return att.id === selectedEvent.value?.announceDetail?.highlightedImageIdx;
  }
  if (!highlightedImageId.value) {
    return false;
  }
  return Number(props.attachments[ak.key].attachment.id) === highlightedImageId.value;
};

const isAttachmentReady = (key: AttachmentKey) => {
  const att = pendingAttachments.value[key.key]?.attachment;
  if (!key.pending || (att && att.status == AttachmentStatus.READY)) {
    return true;
  }
  return false;
};

const hasAttachmentFailed = (key: AttachmentKey) =>
  pendingAttachments.value[key.key] && pendingAttachments.value[key.key].attachment.status === AttachmentStatus.FAILED;

const setCover = (ak: AttachmentKey) => {
  closeModalAndReset();
  let id = null;
  if (ak.pending) {
    id = pendingAttachments.value[ak.key].attachment.id;
  } else {
    id = props.attachments[ak.key].attachment.id;
  }
  highlightedImageId.value = Number(id);
  prepareReadyAttachments(attachmentIds.value);
};

const onImageSelected = async () => {
  const files = imgInput.value?.files;
  if (!files || files.length === 0) {
    return;
  }
  await openEditor(files[0]);
};

const editImg = async (imgId: AttachmentKey) => {
  closeModalAndReset();
  const imgSrc = attachmentUrl(imgId);
  if (!imgSrc) {
    return;
  }
  await openEditor(imgSrc);
};

const openEditor = async (imgSrc: ImageSource | string) => {
  const editor = openDefaultEditor({
    src: imgSrc,
    cropSelectPresetOptions: [
      [1, 'Square'],
      [4 / 3, 'Landscape 4/3'],
      [3 / 4, 'Portrait 3/4'],
      [16 / 9, 'Portrait 16/9'],
    ],
  });

  // Fires when we're done editing the image
  editor.on('process', async ({ dest: file }) => {
    const keys = attachmentStore.saveFiles(convertToFilesList([file]));
    attachmentKeys.value.push(...keys.map(key => ({ pending: true, key, id: null })));
    loadPreviews();
  });
};

const convertToFilesList = (files: any[]): FileList => {
  const dataTransfer = new DataTransfer();
  files.forEach((file: any) => {
    dataTransfer.items.add(new File([file], file.name));
  });
  return dataTransfer.files;
};

const isPendingAttachmentUploaded = computed(() => {
  for (const key of pendingAttachmentKeys.value) {
    if (
      pendingAttachments.value[key].uploadProgress !== 100 ||
      !pendingAttachments.value[key].metadataSaved ||
      pendingAttachments.value[key].attachment.status !== 'ready'
    ) {
      return false;
    }
  }

  return true;
});

watch(isPendingAttachmentUploaded, () => {
  // SetTimeout is workaround for attachment url not being ready yet in s3
  // Solution would be to set attachments prop only on initial load and for
  // the rest push the pending attachment keys
  initHighlightedImageId();
  setTimeout(() => {
    for (const key of pendingAttachmentKeys.value) {
      const id = attachmentStore.getPendingAttachmentId(key as string);
      if (id !== null) {
        addAttachmentId(id);
      }
    }
  }, 2000);
});

const attachmentsWatcher = computed(() => props.attachments);
watch(attachmentsWatcher, newValue => {
  if (newValue.length > 0) {
    setAttachmentProperties();
  }
});

const highlightedImageIdExistInAttachmentIds = computed(() => {
  if (highlightedImageId.value === null) return false;
  return attachmentIds.value.includes(highlightedImageId.value);
});
watch(highlightedImageIdExistInAttachmentIds, () => {
  if (!highlightedImageIdExistInAttachmentIds.value) {
    const firstAvailableId = attachmentIds.value[0] || null;
    highlightedImageId.value = firstAvailableId;
  }
});

const addAttachmentId = (id: number) => {
  const ids = [...attachmentIds.value];

  if (ids.includes(id)) {
    return;
  }

  if (attachmentIds.value.length === 0) {
    highlightedImageId.value = id;
  }
  ids.push(id);
  prepareReadyAttachments(ids);
};

const loadPreviews = () => {
  for (const key of pendingAttachmentKeys.value) {
    if (!attachmentPreviews.value[key]) {
      attachmentPreviews.value[key] = {
        loaded: false,
      };
    }

    if (attachmentPreviews.value[key].loaded) {
      continue;
    }

    const fileReader = new FileReader();
    fileReader.addEventListener('load', function () {
      attachmentPreviews.value[key].loaded = true;
      attachmentPreviews.value[key].data = this.result?.toString();
    });
    fileReader.readAsDataURL(pendingAttachments.value[key].file);
  }
};

const addImage = () => {
  if (!allAttachmentsReady.value) {
    return false;
  }
  imgInput.value?.click();
};

const deleteImg = (index: number, attachment: AttachmentKey) => {
  closeModalAndReset();
  const key = attachment.key;
  const deletedId = attachment.pending
    ? pendingAttachments.value[key].attachment.id
    : attachmentKeys.value.find(e => e.key === key).id;
  deleteAttachmentIds.value.push(deletedId);
  handleHighlightedImage(index);
  attachmentKeys.value = attachmentKeys.value.filter((_, i) => i !== index);

  prepareReadyAttachments(attachmentIds.value);
};

const handleHighlightedImage = (deletedImgIndex: number): void => {
  if (attachmentIds.value.length === 1) {
    highlightedImageId.value = null;
  }
  const deletedImgId = attachmentKeys.value[deletedImgIndex].id;
  const isDeletedIndexHighlighted = props.attachments.find(
    attachment => attachment.attachment.id === deletedImgId,
  )?.highlighted;
  if (isDeletedIndexHighlighted) {
    const newHighlightedImgId = attachmentIds.value.find(id => !deleteAttachmentIds.value.includes(id));
    highlightedImageId.value = newHighlightedImgId || null;
  }
};

/**
 * Returns either real attachment url (https://....) or data url, depending on
 * the upload status
 */
const attachmentUrl = (key: AttachmentKey): string | undefined => {
  if (key.pending) {
    const att = pendingAttachments.value[key.key].attachment;
    if (att.status == AttachmentStatus.READY) {
      return att.url;
    }

    return attachmentPreviews.value[key.key].data;
  }

  return props.attachments[key.key]?.attachment.url;
};

const onFilePreviewClick = (attachment: AttachmentKey, index: number) => {
  if (!isAttachmentReady(attachment)) {
    return;
  }
  activeAttachmentPreview.value = attachment;
  toggleImagePreview.value = true;
  activeAttachmentIndexPreview.value = index;
};

const closeModalAndReset = () => {
  toggleImagePreview.value = false;
  activeAttachmentPreview.value = null;
  activeAttachmentIndexPreview.value = null;
};

const initHighlightedImageId = () => {
  const highlightedAttachment = props.attachments.find(a => a.highlighted);
  if (highlightedAttachment) {
    return (highlightedImageId.value = Number(highlightedAttachment.attachment.id));
  }
  if (attachmentIds.value.length === 0) {
    return (highlightedImageId.value = null);
  }
  if (attachmentIds.value.length === 1) {
    highlightedImageId.value = attachmentIds.value[0];
  }
};
</script>
